import React, { useState, useEffect, useMemo } from "react";
import MantenimientoCard from "./MantenimientoCard";
import ModalCrearMantenimiento from "./ModalCrearMantenimiento";
import {
  listarMantenimientos,
  listarVehiculos,
  listarMaquinaria,
  listarUtillaje,
  listarAlmacen,
  borrarMantenimiento,
} from "../api";
import "../styles.css";

function DashboardMantenimientos({ usuario, onLogout }) {
  const esAdmin = usuario?.rol === "admin";
  const puedeEditar = esAdmin || usuario?.rol === "user";

  const [mantenimientos, setMantenimientos] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [maquinaria, setMaquinaria] = useState([]);
  const [utillaje, setUtillaje] = useState([]);
  const [almacenItems, setAlmacenItems] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [mantenimientoSeleccionado, setMantenimientoSeleccionado] =
    useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(8);

  useEffect(() => {
    const cargarDatos = async () => {
      const [mant, veh, maq, uti, items] = await Promise.all([
        listarMantenimientos(),
        listarVehiculos(),
        listarMaquinaria(),
        listarUtillaje(),
        listarAlmacen(),
      ]);

      const mantOrdenados = mant.sort(
        (a, b) => new Date(b.fecha) - new Date(a.fecha)
      );

      setMantenimientos(mantOrdenados);
      setVehiculos(veh);
      setMaquinaria(maq);
      setUtillaje(uti);
      setAlmacenItems(items);
    };
    cargarDatos();
  }, []);

  const filtrados = useMemo(() => {
    if (!busqueda) return mantenimientos;
    const texto = busqueda.toLowerCase();
    return mantenimientos.filter((m) => {
      const desc = (m.descripcion || "").toLowerCase();
      const equipoNombre =
        m.vehiculo?.nombre ||
        m.maquinaria?.nombre ||
        m.utillaje?.nombre ||
        "";
      const matricula =
        m.vehiculo?.matricula ||
        m.maquinaria?.matricula ||
        m.utillaje?.matricula ||
        "";
      return (
        desc.includes(texto) ||
        equipoNombre.toLowerCase().includes(texto) ||
        matricula.toLowerCase().includes(texto)
      );
    });
  }, [mantenimientos, busqueda]);

  const totalItems = filtrados.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = page * pageSize;
  const end = start + pageSize;
  const currentPageItems = filtrados.slice(start, end);

  const goPrev = () => setPage((p) => Math.max(0, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));
  const goTo = (p) => setPage(Math.max(0, Math.min(totalPages - 1, p)));

  useEffect(() => setPage(0), [busqueda, pageSize]);

  const handleEliminar = async (mantenimiento) => {
    if (!esAdmin) return;
    if (!window.confirm("¿Eliminar este mantenimiento?")) return;
    try {
      await borrarMantenimiento(mantenimiento.id);
      setMantenimientos((prev) =>
        prev.filter((m) => m.id !== mantenimiento.id)
      );
    } catch (err) {
      console.error("Error eliminando mantenimiento:", err);
    }
  };

  const abrirModalEditar = (mantenimiento) => {
    if (!puedeEditar) return;
    setMantenimientoSeleccionado(mantenimiento);
    setShowModal(true);
  };

  const abrirModalCrear = () => {
    if (!puedeEditar) return;
    setMantenimientoSeleccionado(null);
    setShowModal(true);
  };

  return (
    <div className="dashboard-container" style={{ minHeight: "100vh" }}>
      <div className="dashboard-header">
        <h1>Mantenimientos</h1>
      </div>

      <div className="dashboard-controls">
        <input
          type="text"
          placeholder="Buscar mantenimientos..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="buscador"
        />
        {puedeEditar && (
          <button className="botonAzul crear-equipo" onClick={abrirModalCrear}>
            + Crear Mantenimiento
          </button>
        )}
      </div>

      <div className="tabla-container">
        <div className="lista-encabezado">
          <div className="col-titulo">Nombre</div>
          <div className="col-matricula">Matrícula / SN</div>
          <div className="col-fecha">Fecha</div>
          <div className="col-descripcion">Descripción</div>
          <div className="col-acciones">Acciones</div>
        </div>

        {currentPageItems.length > 0 ? (
          currentPageItems.map((m) => (
            <MantenimientoCard
              key={m.id}
              mantenimiento={m}
              almacenItems={almacenItems}
              onEditar={abrirModalEditar}
              onEliminar={handleEliminar}
              puedeEditar={puedeEditar}
              puedeBorrar={esAdmin}
            />
          ))
        ) : (
          <div className="no-resultados">No hay resultados.</div>
        )}
      </div>

      <div className="paginacion-container">
        <div>
          <label>
            Mostrar:{" "}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setPage(0);
              }}
              style={{ marginLeft: 5 }}
            >
              <option value={8}>8</option>
              <option value={15}>15</option>
              <option value={50}>50</option>
            </select>
          </label>
        </div>

        <div
          className="paginacion-numeros"
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            marginTop: "10px",
          }}
        >
          <button onClick={goPrev} disabled={page === 0}>
            Anterior
          </button>
          {Array.from({ length: totalPages }).map((_, idx) => (
            <button
              key={idx}
              className={idx === page ? "activo" : ""}
              onClick={() => goTo(idx)}
            >
              {idx + 1}
            </button>
          ))}
          <button onClick={goNext} disabled={page === totalPages - 1}>
            Siguiente
          </button>
        </div>
      </div>

      {showModal && (
        <ModalCrearMantenimiento
          mantenimiento={mantenimientoSeleccionado}
          vehiculos={vehiculos || []}
          maquinaria={maquinaria || []}
          utillaje={utillaje || []}
          onGuardar={() => {
            window.location.reload();
          }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

export default DashboardMantenimientos;
